import { Component, DestroyRef, ElementRef, HostListener, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { filter } from 'rxjs';
import { AttendanceOperationsService, DayView } from '../../../../../application/services/Hr-Managment/attendance/attendance-processing.service';
import { AttEmployeeDailyAbsentService } from '../../../../../application/services/Hr-Managment/attendance/att-employee-daily-absent.service';
import { AttEmployeeDailyDeductionService } from '../../../../../application/services/Hr-Managment/attendance/att-employee-daily-deduction.service';
import { EmployeeOvertimeService } from '../../../../../application/services/Hr-Managment/attendance/employee-overtime.service';
import { PermissionsService } from '../../../../../application/services/system/permissions.service';
import { LanguageService } from '../../../../../application/services/language.service';
import { ConfirmDialogService } from '../../../../../shared/confirm-dialog/confirm-dialog.service';
import { AttendanceDayDrawerService } from './attendance-day-drawer.service';

type DecisionKind = 'absence' | 'deduction' | 'correction' | 'overtime';

/** The roster screen (a bare page code); the drawer links to the same employee and day. */
const ROSTER_SCREEN = '030800020010';

/**
 * att_review_10: the employee-day drawer (GET api/Attendance/day). One place to see why a day looks the way it does: the planned shift,
 * every punch with its evidence, the daily row, the absence / delay decisions, overtime, vacations, leaves, the corrections timeline,
 * the processing runs and whether the day waits for a re-process; and to act on it by right (re-process, correct, approve / reject).
 */
@Component({
  selector: 'app-attendance-day-drawer',
  imports: [FormsModule, TranslateModule, NgTemplateOutlet],
  template: `
@if (drawer.current(); as target) {
<div class="modal-backdrop !p-0 !items-stretch justify-end">
<aside #panel class="modal !max-w-xl !max-h-none h-full !rounded-none" role="dialog" aria-modal="true" tabindex="-1"
  [attr.aria-label]="'attendanceDay.TITLE' | translate" data-testid="day-drawer" (keydown)="trapTab($event)">
  <header class="sticky top-0 bg-surface border-b z-10 px-4 py-3 flex items-start justify-between gap-3">
    <div>
      <h2 class="text-lg font-semibold">{{ 'attendanceDay.TITLE' | translate }}</h2>
      <p class="text-sm text-muted">{{ employeeName() }} · <span dir="ltr">{{ target.date }}</span></p>
    </div>
    <button class="btn btn-sm btn-outline-secondary" type="button" data-action="close-drawer" (click)="drawer.close()"
      [attr.aria-label]="'attendanceDay.CLOSE' | translate">✕</button>
  </header>

  <div class="p-4 space-y-4 text-sm">
    @if (loading()) { <p class="text-muted">{{ 'attendanceDay.LOADING' | translate }}</p> }
    @if (error()) { <p class="text-danger-600" role="alert">{{ error() }}</p> }

    @if (view(); as v) {
    @if (v.monthClosed) { <p class="border border-warning-200 bg-warning-50 text-warning-900 rounded-lg p-2" role="status">{{ 'attendanceDay.MONTH_CLOSED' | translate }}</p> }

    <div class="flex flex-wrap gap-2">
      @if (canUpdate && !v.monthClosed) {
        <button class="btn btn-sm btn-primary" type="button" data-action="reprocess" (click)="reprocess()" [disabled]="busy()">{{ 'attendanceDay.REPROCESS' | translate }}</button>
        <button class="btn btn-sm btn-outline-primary" type="button" data-action="new-correction" (click)="newCorrection()" [disabled]="busy()">{{ 'attendanceDay.NEW_CORRECTION' | translate }}</button>
      }
      <button class="btn btn-sm btn-outline-secondary" type="button" data-action="roster" (click)="openRoster()">{{ 'attendanceDay.ROSTER_DAY' | translate }}</button>
    </div>

    @if (correction) {
    <form class="border rounded-lg p-3 grid gap-3 grid-cols-2" (ngSubmit)="submitCorrection()" data-testid="correction-form">
      <label class="flex items-center gap-2 col-span-2"><input type="checkbox" name="dayExcuse" [(ngModel)]="correction.excuse">
        {{ 'attendanceOps.CORRECTIONS.excuse' | translate }}</label>
      @if (!correction.excuse) {
      <label class="block">{{ 'attendanceOps.CORRECTIONS.checkIn' | translate }}
        <input class="form-control" type="time" name="dayIn" [(ngModel)]="correction.checkIn"></label>
      <label class="block">{{ 'attendanceOps.CORRECTIONS.checkOut' | translate }}
        <input class="form-control" type="time" name="dayOut" [(ngModel)]="correction.checkOut"></label>
      }
      <label class="block col-span-2">{{ 'attendanceOps.CORRECTIONS.reason' | translate }}
        <input class="form-control" type="text" maxlength="500" name="dayReason" [(ngModel)]="correction.reason"></label>
      <div class="col-span-2 flex gap-2">
        <button class="btn btn-sm btn-primary" type="submit" [disabled]="busy()">{{ 'attendanceOps.CORRECTIONS.save' | translate }}</button>
        <button class="btn btn-sm btn-outline-secondary" type="button" (click)="correction = null">{{ 'attendanceDay.CANCEL' | translate }}</button>
      </div>
    </form>
    }

    <section data-section="planned" class="space-y-1">
      <h3 class="font-semibold">{{ 'attendanceDay.SECTIONS.planned' | translate }}</h3>
      @if (v.planned; as p) {
        <dl class="grid grid-cols-2 gap-x-3 gap-y-1">
          <dt class="text-muted">{{ 'attendanceDay.F.shift' | translate }}</dt><dd>{{ lang() === 'ar' ? (p.shiftAr || p.shiftEn) : (p.shiftEn || p.shiftAr) || '—' }}</dd>
          @if (p.offKind && p.offKind !== 'None') { <dt class="text-muted">{{ 'attendanceDay.F.off' | translate }}</dt><dd>{{ p.offKind }}</dd> }
          @if (p.isRamadan) { <dt class="text-muted">{{ 'attendanceDay.F.ramadan' | translate }}</dt><dd>✓</dd> }
          <dt class="text-muted">{{ 'attendanceDay.F.plannedTime' | translate }}</dt><dd dir="ltr">{{ hm(p.plannedStart) }} → {{ hm(p.plannedEnd) }}</dd>
          <dt class="text-muted">{{ 'attendanceDay.F.grace' | translate }}</dt><dd>{{ p.graceMinutes ?? '—' }}</dd>
          <dt class="text-muted">{{ 'attendanceDay.F.claim' | translate }}</dt><dd dir="ltr">{{ hm(p.claimFrom) }} → {{ hm(p.claimTo) }}</dd>
        </dl>
      } @else { <p class="text-muted">{{ 'attendanceDay.NONE' | translate }}</p> }
    </section>

    <section data-section="punches" class="space-y-1">
      <h3 class="font-semibold">{{ 'attendanceDay.SECTIONS.punches' | translate }}</h3>
      @if (v.punches?.length) {
      <ul class="divide-y border rounded-lg">
        @for (p of v.punches; track p.id) {
        <li class="p-2 flex flex-wrap items-center gap-x-3 gap-y-1" [class.opacity-60]="!p.active">
          <span class="font-medium" dir="ltr">{{ hm(p.at) }}</span>
          <span>{{ p.source }}@if (p.method) { · {{ p.method }} }@if (p.machineName) { · {{ p.machineName }} }</span>
          @if (!p.active) { <span class="badge">{{ 'attendanceDay.F.inactive' | translate }}</span> }
          @if (p.correctionId) { <span class="badge">{{ 'attendanceDay.F.correction' | translate }} #{{ p.correctionId }}</span> }
          @for (f of p.flags ?? []; track f) { <span class="badge text-danger-600">{{ f }}</span> }
          @if (mapHref(p); as href) {
            <a class="text-primary underline" data-testid="map-link" [href]="href" target="_blank" rel="noopener noreferrer">{{ 'attendanceDay.MAP' | translate }}</a>
          }
        </li>
        }
      </ul>
      } @else { <p class="text-muted">{{ 'attendanceDay.NONE' | translate }}</p> }
    </section>

    <section data-section="daily" class="space-y-1">
      <h3 class="font-semibold">{{ 'attendanceDay.SECTIONS.daily' | translate }}</h3>
      @if (v.daily; as d) {
        <dl class="grid grid-cols-2 gap-x-3 gap-y-1">
          <dt class="text-muted">{{ 'attendanceDay.F.status' | translate }}</dt><dd>{{ d.status || '—' }}</dd>
          <dt class="text-muted">{{ 'attendanceDay.F.inOut' | translate }}</dt><dd dir="ltr">{{ hm(d.checkIn) }} → {{ hm(d.checkOut) }}</dd>
          <dt class="text-muted">{{ 'attendanceDay.F.delay' | translate }}</dt><dd>{{ d.delayMinutes ?? 0 }}</dd>
          <dt class="text-muted">{{ 'attendanceDay.F.early' | translate }}</dt><dd>{{ d.earlyLeaveMinutes ?? 0 }}</dd>
          <dt class="text-muted">{{ 'hrReports.c.workedMinutes' | translate }}</dt><dd>{{ d.workedMinutes ?? 0 }}</dd>
          @if (d.notes) { <dt class="text-muted">{{ 'attendanceDay.NOTE' | translate }}</dt><dd>{{ d.notes }}</dd> }
        </dl>
      } @else { <p class="text-muted">{{ 'attendanceDay.NONE' | translate }}</p> }
    </section>

    <section data-section="absences" class="space-y-1">
      <h3 class="font-semibold">{{ 'attendanceDay.SECTIONS.absences' | translate }}</h3>
      @for (a of v.absences ?? []; track a.id) {
        <div class="border rounded-lg p-2 flex flex-wrap items-center gap-2">
          <span>#{{ a.id }}</span><span>{{ statusLabel(a.status) }}</span><span>{{ a.deductionPercentage ?? 0 }}%</span>
          @if (a.noRuleMatched) { <span class="badge">{{ 'attendanceDay.F.noRule' | translate }}</span> }
          <ng-container *ngTemplateOutlet="actions; context: { kind: 'absence', id: a.id, status: a.status }"></ng-container>
        </div>
      } @empty { <p class="text-muted">{{ 'attendanceDay.NONE' | translate }}</p> }
    </section>

    <section data-section="deductions" class="space-y-1">
      <h3 class="font-semibold">{{ 'attendanceDay.SECTIONS.deductions' | translate }}</h3>
      @for (d of v.deductions ?? []; track d.id) {
        <div class="border rounded-lg p-2 flex flex-wrap items-center gap-2">
          <span>#{{ d.id }}</span><span>{{ d.type }}</span><span>{{ d.violationMinutes ?? 0 }} {{ 'attendanceDay.F.min' | translate }}</span>
          <span>{{ statusLabel(d.status) }}</span><span>{{ d.deductionPercentage ?? 0 }}%</span>
          @if (d.noRuleMatched) { <span class="badge">{{ 'attendanceDay.F.noRule' | translate }}</span> }
          <ng-container *ngTemplateOutlet="actions; context: { kind: 'deduction', id: d.id, status: d.status }"></ng-container>
        </div>
      } @empty { <p class="text-muted">{{ 'attendanceDay.NONE' | translate }}</p> }
    </section>

    <section data-section="overtime" class="space-y-1">
      <h3 class="font-semibold">{{ 'attendanceDay.SECTIONS.overtime' | translate }}</h3>
      @if (v.overtime; as o) {
        <div class="border rounded-lg p-2 flex flex-wrap items-center gap-2">
          <span>#{{ o.id }}</span><span>{{ o.minutes }} {{ 'attendanceDay.F.min' | translate }}</span>@if (o.kind) { <span>{{ o.kind }}</span> }<span>{{ statusLabel(o.status) }}</span>
          <ng-container *ngTemplateOutlet="actions; context: { kind: 'overtime', id: o.id, status: o.status }"></ng-container>
        </div>
      } @else { <p class="text-muted">{{ 'attendanceDay.NONE' | translate }}</p> }
    </section>

    <section data-section="vacations" class="space-y-1">
      <h3 class="font-semibold">{{ 'attendanceDay.SECTIONS.vacations' | translate }}</h3>
      @for (x of v.vacations ?? []; track x.id) {
        <div class="border rounded-lg p-2">#{{ x.id }} · <span dir="ltr">{{ hm(x.from, true) }} → {{ hm(x.to, true) }}</span> · {{ statusLabel(x.status) }}</div>
      } @empty { <p class="text-muted">{{ 'attendanceDay.NONE' | translate }}</p> }
    </section>

    <section data-section="leaves" class="space-y-1">
      <h3 class="font-semibold">{{ 'attendanceDay.SECTIONS.leaves' | translate }}</h3>
      @for (x of v.leaves ?? []; track x.id) {
        <div class="border rounded-lg p-2">#{{ x.id }} · <span dir="ltr">{{ hm(x.from) }} → {{ hm(x.to) }}</span> · {{ statusLabel(x.status) }}</div>
      } @empty { <p class="text-muted">{{ 'attendanceDay.NONE' | translate }}</p> }
    </section>

    <section data-section="corrections" class="space-y-1">
      <h3 class="font-semibold">{{ 'attendanceDay.SECTIONS.corrections' | translate }}</h3>
      @for (c of v.corrections ?? []; track c.id) {
        <div class="border rounded-lg p-2 space-y-1">
          <div class="flex flex-wrap items-center gap-2">
            <span>#{{ c.id }}</span><span>{{ statusLabel(c.status) }}</span>
            @if (c.excuse) { <span>{{ 'attendanceOps.CORRECTIONS.excused' | translate }}</span> } @else { <span dir="ltr">{{ hm(c.checkIn) }} → {{ hm(c.checkOut) }}</span> }
            <ng-container *ngTemplateOutlet="actions; context: { kind: 'correction', id: c.id, status: c.status }"></ng-container>
          </div>
          @if (c.reason) { <div class="text-muted">{{ c.reason }}</div> }
          @if (c.timeline?.length) {
          <ol class="ps-4 list-decimal text-xs text-muted">
            @for (t of c.timeline; track $index) { <li>{{ t.step }} · <span dir="ltr">{{ hm(t.at, true) }}</span>@if (t.byId) { · {{ t.byId }} }</li> }
          </ol>
          }
        </div>
      } @empty { <p class="text-muted">{{ 'attendanceDay.NONE' | translate }}</p> }
    </section>

    <section data-section="runs" class="space-y-1">
      <h3 class="font-semibold">{{ 'attendanceDay.SECTIONS.runs' | translate }}</h3>
      @for (r of v.runs ?? []; track r.id) {
        <div class="border rounded-lg p-2">#{{ r.id }} · {{ r.trigger }} · <span dir="ltr">{{ hm(r.startedAt, true) }}</span></div>
      } @empty { <p class="text-muted">{{ 'attendanceDay.NONE' | translate }}</p> }
    </section>

    <section data-section="dirty" class="space-y-1">
      <h3 class="font-semibold">{{ 'attendanceDay.SECTIONS.dirty' | translate }}</h3>
      @if (v.dirty?.waiting) {
        <p class="text-warning-900">{{ 'attendanceDay.DIRTY_WAITING' | translate }}@if (v.dirty?.reason) { · {{ v.dirty?.reason }} }@if (v.dirty?.markedAt) { · <span dir="ltr">{{ hm(v.dirty?.markedAt, true) }}</span> }</p>
      } @else { <p class="text-muted">{{ 'attendanceDay.DIRTY_CLEAN' | translate }}</p> }
    </section>
    }
  </div>
</aside>
</div>

<ng-template #actions let-kind="kind" let-id="id" let-status="status">
  @if (canApprove && status === 'Pending' && !view()?.monthClosed) {
    <span class="ms-auto whitespace-nowrap space-x-1">
      <button class="btn btn-sm btn-primary" type="button" data-action="approve" [attr.data-kind]="kind" [attr.data-id]="id" (click)="decide(kind, id, true)" [disabled]="busy()">{{ 'attendanceDay.APPROVE' | translate }}</button>
      <button class="btn btn-sm btn-outline-danger" type="button" data-action="reject" [attr.data-kind]="kind" [attr.data-id]="id" (click)="decide(kind, id, false)" [disabled]="busy()">{{ 'attendanceDay.REJECT' | translate }}</button>
    </span>
  }
</ng-template>
}`,
})
export class AttendanceDayDrawerComponent {
  readonly drawer = inject(AttendanceDayDrawerService);
  private ops = inject(AttendanceOperationsService);
  private absences = inject(AttEmployeeDailyAbsentService);
  private deductions = inject(AttEmployeeDailyDeductionService);
  private overtime = inject(EmployeeOvertimeService);
  private permissions = inject(PermissionsService);
  private router = inject(Router);
  private confirm = inject(ConfirmDialogService);
  private i18n = inject(TranslateService);
  private toastr = inject(ToastrService);
  private langs = inject(LanguageService);
  private destroyRef = inject(DestroyRef);

  readonly view = signal<DayView | null>(null);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly lang = signal<'ar' | 'en'>('ar');
  canUpdate = false;
  canApprove = false;
  /** The correction being typed (prefilled from the daily row); null when the form is closed. */
  correction: { employeeId: number; date: string; checkIn: string; checkOut: string; excuse: boolean; reason: string } | null = null;
  private loadToken = 0;

  constructor() {
    this.lang.set(this.langs.getCurrent() === 'en' ? 'en' : 'ar');
    this.langs.currentLang$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(l => this.lang.set(l === 'en' ? 'en' : 'ar'));
    this.permissions.getPermissions().pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef)).subscribe(p => {
      this.canUpdate = !!p.canUpdate;
      this.canApprove = !!p.canApprove;
    });
    effect(() => {
      const target = this.drawer.current();
      untracked(() => {
        this.correction = null;
        this.error.set('');
        if (target) {
          // remember where the focus was (the row's "Open day" button) and move it into the drawer
          if (!this.returnFocus) this.returnFocus = typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null;
          setTimeout(() => this.focusFirst());
          this.load();
        } else {
          this.view.set(null);
          this.loadToken++;
          const back = this.returnFocus;
          this.returnFocus = null;
          if (back && typeof back.focus === 'function' && back.isConnected) setTimeout(() => back.focus());
        }
      });
    });
  }

  /** The element focused before the drawer opened; it gets the focus back when the drawer closes. */
  private returnFocus: HTMLElement | null = null;
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  private focusables(): HTMLElement[] {
    const root = this.panel()?.nativeElement;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'));
  }

  private focusFirst() {
    const root = this.panel()?.nativeElement;
    if (!root) return;
    const close = root.querySelector<HTMLElement>('[data-action="close-drawer"]');
    (close ?? this.focusables()[0] ?? root).focus();
  }

  /** Focus trap (WAI-ARIA modal dialog): Tab / Shift+Tab cycle inside the drawer. */
  trapTab(event: KeyboardEvent) {
    if (event.key !== 'Tab') return;
    const root = this.panel()?.nativeElement;
    const items = this.focusables();
    if (!root) return;
    if (items.length === 0) { event.preventDefault(); root.focus(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement as HTMLElement | null;
    const inside = !!active && root.contains(active) && active !== root;
    if (event.shiftKey && (!inside || active === first)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (!inside || active === last)) { event.preventDefault(); first.focus(); }
  }

  @HostListener('document:keydown.escape')
  onEscape() { if (this.drawer.current()) this.drawer.close(); }

  employeeName(): string {
    const v = this.view();
    if (!v) return '';
    return (this.lang() === 'ar' ? v.employeeNameAr || v.employeeNameEn : v.employeeNameEn || v.employeeNameAr) || `#${v.employeeId}`;
  }

  /** HH:mm of a time ('08:25:00' or a local ISO date-time); with `withDate` the ISO date-time keeps its day. */
  hm(value?: string | null, withDate = false): string {
    if (!value) return '—';
    if (value.includes('T')) return withDate ? `${value.slice(0, 10)} ${value.slice(11, 16)}` : value.slice(11, 16);
    return value.slice(0, 5);
  }

  statusLabel(status: string): string {
    const key = `attendanceOps.CORRECTIONS.${status}`;
    const text = this.i18n.instant(key);
    return text === key ? status : text;
  }

  mapHref(p: { latitude?: number | null; longitude?: number | null; mapUrl?: string | null }): string | null {
    if (p.mapUrl) return p.mapUrl;
    return p.latitude != null && p.longitude != null ? `https://www.google.com/maps?q=${p.latitude},${p.longitude}` : null;
  }

  private load() {
    const target = this.drawer.current();
    if (!target) return;
    const token = ++this.loadToken;
    this.loading.set(true);
    this.ops.day(target.employeeId, target.date).subscribe({
      next: res => { if (token !== this.loadToken) return; this.loading.set(false); this.view.set(res?.data ?? null); },
      error: err => { if (token !== this.loadToken) return; this.loading.set(false); this.view.set(null); this.error.set(typeof err === 'string' ? err : err?.message || ''); },
    });
  }

  private fail = (err: any) => { this.busy.set(false); this.toastr.error(typeof err === 'string' ? err : err?.message); this.load(); };

  reprocess() {
    const t = this.drawer.current();
    if (!t) return;
    this.busy.set(true);
    this.ops.reprocessDay(t.employeeId, t.date).subscribe({
      next: res => { this.busy.set(false); if (res?.message) this.toastr.success(res.message); if (res?.data) this.view.set(res.data); else this.load(); },
      error: this.fail,
    });
  }

  newCorrection() {
    const t = this.drawer.current();
    const d = this.view()?.daily;
    if (!t) return;
    this.correction = { employeeId: t.employeeId, date: t.date, checkIn: this.hm(d?.checkIn).replace('—', ''), checkOut: this.hm(d?.checkOut).replace('—', ''), excuse: false, reason: '' };
  }

  submitCorrection() {
    const c = this.correction;
    if (!c) return;
    if (!c.reason.trim() || (!c.excuse && !c.checkIn && !c.checkOut)) { this.toastr.warning(this.i18n.instant('attendance.corrections.incomplete')); return; }
    this.busy.set(true);
    this.ops.createCorrection({
      employeeId: c.employeeId, date: c.date, checkIn: c.excuse ? null : c.checkIn || null, checkOut: c.excuse ? null : c.checkOut || null,
      excuse: c.excuse, reason: c.reason.trim(),
    }).subscribe({
      next: res => { this.busy.set(false); this.toastr.success(res?.message); this.correction = null; this.load(); },
      error: this.fail,
    });
  }

  async decide(kind: DecisionKind, id: number, approve: boolean) {
    const note = await this.confirm.askNote(
      this.i18n.instant(approve ? 'attendance.corrections.confirmApprove' : 'attendance.corrections.confirmReject'),
      this.i18n.instant(approve ? 'attendanceDay.APPROVE' : 'attendanceDay.REJECT'), this.i18n.instant('attendanceDay.NOTE'));
    if (note === null) return;
    const status = approve ? 2 : 3;
    const call = kind === 'absence' ? this.absences.decide(id, status, note)
      : kind === 'deduction' ? this.deductions.decide(id, status, note)
      : kind === 'correction' ? this.ops.decide(id, approve, note)
      : this.overtime.decide([id], approve);
    this.busy.set(true);
    call.subscribe({
      next: (res) => { this.busy.set(false); if (res?.message) this.toastr.success(res.message); this.load(); },
      error: this.fail,
    });
  }

  openRoster() {
    const t = this.drawer.current();
    if (!t) return;
    this.router.navigate(['/screen', ROSTER_SCREEN], { queryParams: { employeeId: t.employeeId, date: t.date } });
    this.drawer.close();
  }
}
